// apps/admin/src/components/auth/MfaCodeInput.tsx
//
// Six-box 6-digit code entry for the native MFA enrolment + challenge screens
// (founder admin portal). Numeric only, auto-advances, backspace steps back,
// and a pasted code fans out across the boxes. Matches the founder-approved MFA
// mockup. (Mirrors apps/client/src/components/auth/MfaCodeInput.tsx.)

import React, { useEffect, useRef, useState } from 'react';

interface MfaCodeInputProps {
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
  disabled?: boolean;
  error?: boolean;
  autoFocus?: boolean;
  resetNonce?: number;
  ariaLabel?: string;
}

const EMPTY: string[] = ['', '', '', '', '', ''];

const MfaCodeInput: React.FC<MfaCodeInputProps> = ({
  onChange,
  onComplete,
  disabled = false,
  error = false,
  autoFocus = false,
  resetNonce = 0,
  ariaLabel = 'Verification code',
}) => {
  const [digits, setDigits] = useState<string[]>(EMPTY);
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    setDigits(EMPTY);
    inputRefs.current[0]?.focus();
  }, [resetNonce]);

  useEffect(() => {
    if (autoFocus) {
      inputRefs.current[0]?.focus();
    }
  }, [autoFocus]);

  const emit = (next: string[]): void => {
    const value = next.join('');
    onChange(value);
    if (value.length === 6 && next.every((digit) => digit !== '')) {
      onComplete?.(value);
    }
  };

  const handleChange = (index: number, raw: string): void => {
    const cleaned = raw.replace(/\D/g, '');
    // OTP autofill (e.g. iOS "one-time-code") injects the full code into a
    // single input's onChange without firing a paste event — fan it out across
    // the boxes rather than truncating to one digit.
    if (cleaned.length > 1) {
      const next = [...EMPTY];
      cleaned.slice(0, 6).split('').forEach((char, i) => {
        next[i] = char;
      });
      setDigits(next);
      inputRefs.current[Math.min(cleaned.length, 6) - 1]?.focus();
      emit(next);
      return;
    }
    const next = [...digits];
    next[index] = cleaned;
    setDigits(next);
    if (cleaned && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
    emit(next);
  };

  const handleKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (event.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (event.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>): void => {
    event.preventDefault();
    const pasted = (event.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;
    const next = [...EMPTY];
    pasted.split('').forEach((char, i) => {
      next[i] = char;
    });
    setDigits(next);
    inputRefs.current[Math.min(pasted.length, 5)]?.focus();
    emit(next);
  };

  return (
    <div className="flex justify-center gap-2" role="group" aria-label={ariaLabel}>
      {digits.map((digit, index) => (
        <input
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          ref={(el) => {
            inputRefs.current[index] = el;
          }}
          type="text"
          inputMode="numeric"
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          maxLength={1}
          value={digit}
          disabled={disabled}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          onFocus={(e) => e.target.select()}
          aria-label={`${ariaLabel} digit ${index + 1}`}
          className={`w-11 h-14 text-center text-2xl font-semibold text-white bg-slate-900/50 border rounded-xl outline-none transition-all duration-150 focus:border-transparent focus:ring-2 focus:ring-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed ${
            error ? 'border-red-500 ring-1 ring-red-500/40' : 'border-slate-600'
          }`}
        />
      ))}
    </div>
  );
};

export default MfaCodeInput;
